import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { setMember, logoutUser } from "../Slices/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactLoading from "react-loading";
import { memberRegisterSchema } from "../schemas";
import mode from "./Mode";
import SearchableSelect from "./SearchableSelect";
import PhoneUpdate from "./phoneUpdate";
import Toast from "./Toaster";
import toast from "react-hot-toast";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import '../styles/member.css';

const MemberRegister = () => {
    const [openPhoneModal, setOpenPhoneModal] = useState(false);
    const user = useSelector((state) => state.auth.user);
    const token = useSelector((state) => state.auth.token);
    const [isLoading, setIsLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    const [coursesLoading, setCoursesLoading] = useState(true);
    const [formMessage, setFormMessage] = useState({ type: '', text: '' });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const baseurl = mode ? "https://backend.portal.jkusdachurch.org/general/" : "http://127.0.0.1:8000/general/";

    const config = useMemo(() => {
        const headers = { "Content-Type": "application/json" };
        if (token) {
            headers["Authorization"] = `Token ${token}`;
        }
        return { headers };
    }, [token]);

    useEffect(() => {
        if (!user) {
            toast.error("Unauthorized, please login.", { duration: 4000, position: "bottom-left" });
            navigate("/login", { state: { reason: "Unauthorized" } });
            return;
        }

        axios.get(baseurl + "courses/", config)
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    setCourses(response.data.map((course) => ({ label: course.name, value: course.id })));
                }
                setCoursesLoading(false);
            })
            .catch(() => {
                setCourses([]);
                setCoursesLoading(false);
                setFormMessage({ type: 'error', text: 'Could not load courses. Please try again later.' });
            });
    }, [user, baseurl, config, navigate, dispatch]);

    const normalizePhone = (phone) => phone.replace(/^(\+254|0)/, "").trim().slice(0, 9);

    const onSubmit = () => {
        setIsLoading(true);
        setFormMessage({ type: '', text: '' });

        const phones = values.phone_numbers.map(p => p.trim()).filter(p => p !== "");
        if (phones.length === 0) {
            setFormMessage({ type: 'error', text: 'At least one phone number is required.' });
            setIsLoading(false);
            return;
        }

        const normalizedPhones = phones.map(normalizePhone);
        const phonePayload = {
            phone_numbers: normalizedPhones,
            primary_phone: normalizedPhones[0],
        };

        const selectedCourse = courses.find((c) => c.label === values.course);
        const payload = {
            first_name: values.first_name,
            last_name: values.last_name,
            ...phonePayload,
            course: selectedCourse?.label || "",
            baptised: Number(values.baptised),
            intake: Number(values.intake),
            gender: values.gender,
            reg_no: values.reg_no,
            next_of_kin: values.next_of_kin,
            home_church: values.home_church,
        };

        axios.post(`${baseurl}add-member/${user.id}/`, payload, config)
            .then((response) => {
                dispatch(setMember(response.data));
                toast.success("Registration Successful!");
                setTimeout(() => navigate("/dashboard"), 3000);
            })
            .catch((err) => {
                const errorMsg = err.response?.data?.Error || "An unexpected error occurred.";
                setFormMessage({ type: 'error', text: errorMsg });
                if (err.response?.status === 401) {
                    dispatch(logoutUser());
                    navigate("/login");
                }
                setIsLoading(false);
            });
    };

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue } = useFormik({
        initialValues: {
            first_name: "",
            last_name: "",
            phone_numbers: [""],
            course: "",
            next_of_kin: "",
            home_church: "",
            baptised: "",
            intake: "",
            gender: "",
            reg_no: "",
        },
        validationSchema: memberRegisterSchema,
        onSubmit,
    });

    return (
        <div className='member-container'>
            <Toast />
            <PhoneUpdate
                page="member"
                open={openPhoneModal}
                onClose={() => setOpenPhoneModal(false)}
                values={values}
                setFieldValue={setFieldValue}
                onSave={() => setOpenPhoneModal(false)}
            />
            <div className="member-wrapper">
                <div className="member-header">
                    <h1>Member Details</h1>
                    <p>Please fill in your information to complete registration.</p>
                </div>

                {coursesLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                        <ReactLoading type="spin" color="#228bdf" height={50} width={50} />
                    </div>
                ) : courses.length === 0 ? (
                    <div className="form-message error">
                        Course information is currently unavailable. Registration is closed.
                    </div>
                ) : (
                    <form method="POST" className='member-form' onSubmit={handleSubmit}>
                        {formMessage.text && (
                            <div className={`form-message ${formMessage.type}`}>
                                {formMessage.text}
                            </div>
                        )}
                        <div className="form-row">
                            <div className='form-group'>
                                <label htmlFor="first_name">First Name</label>
                                <TextField fullWidth required error={!!(errors.first_name && touched.first_name)} id="first_name" variant="outlined" name="first_name" value={values.first_name} onChange={handleChange} onBlur={handleBlur} />
                                {errors.first_name && touched.first_name && <p className='error-text'>{errors.first_name}</p>}
                            </div>
                            <div className='form-group'>
                                <label htmlFor="last_name">Last Name</label>
                                <TextField fullWidth required error={!!(errors.last_name && touched.last_name)} id="last_name" variant="outlined" name="last_name" value={values.last_name} onChange={handleChange} onBlur={handleBlur} />
                                {errors.last_name && touched.last_name && <p className='error-text'>{errors.last_name}</p>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className='form-group'>
                                <label>Phone Numbers</label>
                                <button type="button" className="member-btn secondary" onClick={() => setOpenPhoneModal(true)}>
                                    Update Phones ({values.phone_numbers.filter(p => p.trim()).length})
                                </button>
                                {errors.phone_numbers && touched.phone_numbers && <p className='error-text'>{typeof errors.phone_numbers === 'string' ? errors.phone_numbers : ''}</p>}
                            </div>
                            <div className='form-group'>
                                <label htmlFor="gender">Gender</label>
                                <FormControl fullWidth required error={!!(errors.gender && touched.gender)} variant="outlined">
                                    <Select name="gender" value={values.gender} onChange={handleChange} onBlur={handleBlur} displayEmpty>
                                        <MenuItem value="" disabled><em>Select Gender</em></MenuItem>
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                    </Select>
                                </FormControl>
                                {errors.gender && touched.gender && <p className='error-text'>{errors.gender}</p>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className='form-group'>
                                <label htmlFor="reg_no">Registration Number</label>
                                <TextField fullWidth required error={!!(errors.reg_no && touched.reg_no)} id="reg_no" variant="outlined" name="reg_no" value={values.reg_no} onChange={handleChange} onBlur={handleBlur} />
                                {errors.reg_no && touched.reg_no && <p className='error-text'>{errors.reg_no}</p>}
                            </div>
                            <div className='form-group'>
                                <label htmlFor="course">Course</label>
                                <SearchableSelect
                                    options={courses}
                                    value={values.course}
                                    onChange={(value) => setFieldValue("course", value)}
                                    onBlur={handleBlur}
                                    name="course"
                                    error={!!(errors.course && touched.course)}
                                />
                                {errors.course && touched.course && <p className='error-text'>{errors.course}</p>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className='form-group'>
                                <label htmlFor="intake">Intake/Year</label>
                                <FormControl fullWidth required error={!!(errors.intake && touched.intake)} variant="outlined">
                                    <Select name="intake" value={values.intake} onChange={handleChange} onBlur={handleBlur} displayEmpty>
                                        <MenuItem value="" disabled><em>Select Year</em></MenuItem>
                                        {Array.from({ length: 10 }, (_, i) => {
                                            const year = new Date().getFullYear() - i;
                                            return (<MenuItem key={year} value={year}>{year}</MenuItem>);
                                        })}
                                        <MenuItem value="0000">Associate</MenuItem>
                                    </Select>
                                </FormControl>
                                {errors.intake && touched.intake && <p className='error-text'>{errors.intake}</p>}
                            </div>
                            <div className='form-group'>
                                <label htmlFor="baptised">Baptised</label>
                                <FormControl fullWidth required error={!!(errors.baptised && touched.baptised)} variant="outlined">
                                    <Select name="baptised" value={values.baptised} onChange={handleChange} onBlur={handleBlur} displayEmpty>
                                        <MenuItem value="" disabled><em>Select Status</em></MenuItem>
                                        <MenuItem value={1}>Yes</MenuItem>
                                        <MenuItem value={0}>No</MenuItem>
                                    </Select>
                                </FormControl>
                                {errors.baptised && touched.baptised && <p className='error-text'>{errors.baptised}</p>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className='form-group'>
                                <label htmlFor="next_of_kin">Next of Kin (Name & Phone)</label>
                                <TextField fullWidth required error={!!(errors.next_of_kin && touched.next_of_kin)} id="next_of_kin" variant="outlined" name="next_of_kin" value={values.next_of_kin} onChange={handleChange} onBlur={handleBlur} />
                                {errors.next_of_kin && touched.next_of_kin && <p className='error-text'>{errors.next_of_kin}</p>}
                            </div>
                            <div className='form-group'>
                                <label htmlFor="home_church">Home Church</label>
                                <TextField fullWidth required error={!!(errors.home_church && touched.home_church)} id="home_church" variant="outlined" name="home_church" value={values.home_church} onChange={handleChange} onBlur={handleBlur} />
                                {errors.home_church && touched.home_church && <p className='error-text'>{errors.home_church}</p>}
                            </div>
                        </div>

                        <button className="member-btn" type="submit" disabled={isLoading}>
                            {isLoading ? <ReactLoading type="spin" color="#fff" height={25} width={25} /> : "Complete Registration"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default MemberRegister;